export interface PageMetadata {
  url: string;
  title: string;
  description: string;
  image: string;
}

export function getPageMetadata(): PageMetadata {
  const url = window.location.href;
  const title =
    document
      .querySelector('meta[property="og:title"]')
      ?.getAttribute("content") ||
    document.querySelector('meta[name="title"]')?.getAttribute("content") ||
    document.title;
  const description =
    document
      .querySelector('meta[property="og:description"]')
      ?.getAttribute("content") ||
    document
      .querySelector('meta[name="description"]')
      ?.getAttribute("content") ||
    "";
  const image =
    document
      .querySelector('meta[property="og:image"]')
      ?.getAttribute("content") || "";

  return { url, title, description, image };
}

declare global {
  interface Window {
    meta?: {
      product?: {
        id?: number;
      };
    };
    Shopify?: {
      shop?: string;
      cdnHost?: string;
      analytics?: {
        publish: (eventName: string, payload: Record<string, unknown>) => void;
      };
    };
  }
}

export function getShopifyProductId(): number | null {
  if (typeof window === "undefined") {
    return null;
  }

  const meta = window.meta;
  const productId = meta?.product?.id;

  if (productId) {
    return productId;
  }

  if (import.meta.env.DEV) {
    return 4702236442703;
  }

  return null;
}

export function getShopifyShopId(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  const shopify = window.Shopify;
  const shopId = shopify?.shop;

  if (shopId) {
    return shopId;
  }

  if (import.meta.env.DEV) {
    return "rosenectar.myshopify.com";
  }

  return null;
}

/**
 * Whether the storefront page URL asks reviews to be sorted by the reviewer's
 * order count (desc). Enabled by loading a PDP with `?orders_count=true` (or
 * `=1`); the widget then forwards `orders_count=true` to the reviews endpoint.
 * Only an explicit truthy value enables it, so a stray/`false` value can never
 * flip the sort on — and the backend receives the canonical `true`.
 */
export function isOrderByOrdersCountRequested(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const raw = new URLSearchParams(window.location.search)
    .get("orders_count")
    ?.trim()
    .toLowerCase();

  return raw === "true" || raw === "1";
}

export function getStoreDomain(priorityDomain?: string | null): string | null {
  if (priorityDomain) {
    let domain = priorityDomain.replace(/^https?:\/\//, "");
    domain = domain.split("/")[0];
    domain = domain.replace(/^www\./, "");
    return domain;
  }

  if (typeof window === "undefined") {
    return null;
  }

  const shopify = window.Shopify;
  const cdnHost = shopify?.cdnHost;

  if (cdnHost) {
    let domain = cdnHost.replace(/^https?:\/\//, "");
    domain = domain.split("/")[0];
    domain = domain.replace(/^www\./, "");
    return domain;
  }

  return null;
}

export interface ProductTab {
  id: number;
  label: string;
}

/**
 * Products whose reviews the widget shows as tabs instead of the page's own.
 *
 * A bundle-style product (Pure Rose's rituals) has no reviews of its own --
 * it wraps established products that do. The theme lists them on the mount
 * element as `data-product-tabs='[{"id":3985510694991,"label":"Rose Nectar"}]'`
 * (Shopify product ids), and each becomes a tab loading that product's
 * reviews. Absent, empty or malformed, it returns [] and the widget behaves
 * exactly as before, so no other store or page is affected.
 */
export function getProductTabs(
  mountSelector = "[data-social-ocean-carousel]",
): ProductTab[] {
  if (typeof document === "undefined") return [];
  const raw = document
    .querySelector(mountSelector)
    ?.getAttribute("data-product-tabs");
  if (!raw) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  const seen = new Set<number>();
  const tabs: ProductTab[] = [];
  for (const item of parsed) {
    const id = Number((item as { id?: unknown })?.id);
    const label = (item as { label?: unknown })?.label;
    if (!Number.isSafeInteger(id) || id <= 0 || seen.has(id)) continue;
    if (typeof label !== "string" || !label.trim()) continue;
    seen.add(id);
    tabs.push({ id, label: label.trim() });
  }
  return tabs;
}
